import type { InterviewSummary } from "@/api/generated/types";
import { mapInterviewStatusToDisplay } from "@/lib/interview-ui";
import { toTimestampSlug } from "@/lib/recording-file-name";
import { roleLabels } from "../[candidateId]/_components/role-fit-card";

const CSV_BOM = "﻿";
const CSV_LINE_BREAK = "\r\n";
const CSV_FORMULA_TRIGGER = /^[=+\-@\t\r]/;
const CSV_FILE_PREFIX = "relance-candidates";
const DATE_TIME_PAD_LENGTH = 2;

export const CANDIDATE_CSV_HEADERS = [
  "氏名",
  "メールアドレス",
  "ステータス",
  "AIスコア",
  "判定",
  "最適職種",
  "応募日時",
  "面談完了日時",
  "職務経歴書",
  "詳細URL",
  "面談ID",
] as const;

export function toCsvCell(value: string | number | null): string {
  if (value === null) return '""';
  if (typeof value === "number") return String(value);
  const guarded = CSV_FORMULA_TRIGGER.test(value) ? `'${value}` : value;
  return `"${guarded.replace(/"/g, '""')}"`;
}

function formatCsvDateTime(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const pad = (n: number) => String(n).padStart(DATE_TIME_PAD_LENGTH, "0");
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function buildCandidatesCsv(
  interviews: InterviewSummary[],
  context: { now: Date; origin: string }
): string {
  const rows = interviews.map((interview) =>
    [
      interview.ownerName,
      interview.ownerEmail,
      mapInterviewStatusToDisplay(interview.status, interview.createdAt, context.now),
      interview.overallScore,
      interview.recommendation,
      interview.primaryRole ? (roleLabels[interview.primaryRole] ?? interview.primaryRole) : null,
      formatCsvDateTime(interview.createdAt),
      formatCsvDateTime(interview.completedAt),
      interview.hasResume ? "あり" : "なし",
      `${context.origin}/candidates/${interview.interviewId}`,
      interview.interviewId,
    ]
      .map(toCsvCell)
      .join(",")
  );
  const header = CANDIDATE_CSV_HEADERS.map(toCsvCell).join(",");
  return `${CSV_BOM}${[header, ...rows].join(CSV_LINE_BREAK)}${CSV_LINE_BREAK}`;
}

export function buildCandidatesCsvFileName(now: Date): string {
  return `${CSV_FILE_PREFIX}_${toTimestampSlug(now.toISOString())}.csv`;
}
